import _ from 'lodash';
import { getElementDefinitionProps } from 'crazyEditor/core/elements/utils/elementDefinitionUtils';
import { getPageElement } from 'crazyEditor/core/elements/utils/elementStaticUtils';
import { getDesktopDOMNodeById } from 'crazyEditor/core/elements/utils/elementRefUtils';
import { HEADER_HEIGHT, TOP_PADDING_FROM_TOOLBAR } from 'crazyEditor/core/constants/editorConstants';

function isElementPositionBelowPageElement(elementPosition, pageElementHeight) {
  return elementPosition <= pageElementHeight;
}

function getPositionForFullWidthComponents(newElementPosition, elementLayout) {
  if (newElementPosition.y >= 0) {
    return newElementPosition;
  }
  return {
    y: 0,
    x: elementLayout.x
  };
}

export function isFullWidthComponent(element) {
  return getElementDefinitionProps(element).fullWidth;
}

function getPositionForOverflowingComponents(pageElement, elementLayout, newElementPosition) {
  return {
    y: pageElement.layout.height - elementLayout.height,
    x: newElementPosition.x,
    overflowing: true
  };
}

function getElementPositionBasedOnBoundaries(element, newElementPosition, elementLayout, pageElement) {
  if (isElementPositionBelowPageElement(newElementPosition.y + elementLayout.height, pageElement.layout.height)) {
    if (isFullWidthComponent(element)) {
      return getPositionForFullWidthComponents(newElementPosition, elementLayout);
    }
    return newElementPosition;
  }
  return getPositionForOverflowingComponents(pageElement, elementLayout, newElementPosition);
}

function getUpdatedElementPosition(viewState, state, action, elementId) {
  let element;
  if (_.isObject(elementId)) {
    element = elementId;
  } else {
    element = state[viewState].elements[elementId];
  }
  const elementLayout = element.layout;
  const pageElement = getPageElement(state[viewState].staticElements);

  const newElementPosition = {
    x: action && _.isNumber(action.x) ? action.x : elementLayout.x,
    y: action && _.isNumber(action.y) ? action.y : elementLayout.y
  };

  return getElementPositionBasedOnBoundaries(element, newElementPosition, elementLayout, pageElement);
}

function getUpdatedElementPositionByDelta(viewState, state, action, element, draggableDirections, maxDeltaY) {
  const elementLayout = _.assign({}, element.layout);
  const pageElement = getPageElement(state[viewState].staticElements);

  const newElementPosition = {
    x: _.isNumber(action.deltaX) && draggableDirections.x ? elementLayout.x + action.deltaX : elementLayout.x,
    y: _.isNumber(action.deltaY) && draggableDirections.y ? elementLayout.y + Math.min(maxDeltaY, action.deltaY) : elementLayout.y
  };

  return getElementPositionBasedOnBoundaries(element, newElementPosition, elementLayout, pageElement);
}

function getYPositionAtMiddleOfView() {
  const editorViewHeight = window.innerHeight - HEADER_HEIGHT;
  return (window.scrollY || window.pageYOffset) + editorViewHeight / 2 - TOP_PADDING_FROM_TOOLBAR;
}

function getDraggableDirections(elements) {
  const draggableDirections = {x: true, y: true};
  _.forEach(elements, (element) => {
    const elementDraggableDirections = getElementDefinitionProps(element).draggable;
    draggableDirections.x &= elementDraggableDirections.x;
    draggableDirections.y &= elementDraggableDirections.y;
  });
  return draggableDirections;
}

function getBoundingLayout(elements, pageWidth) {
  let yStart = 0;
  let xStart = 0;
  let yEnd = 0;
  let xEnd = 0;

  let fullWidth = false;
  _.forEach(elements, (element, index) => {
    const layout = element.layout;
    if (index === 0) {
      yStart = layout.y;
      xStart = layout.x;
      xEnd = layout.x + layout.width;
      yEnd = layout.y + layout.height;
    } else {
      yStart = Math.min(yStart, layout.y);
      xStart = Math.min(xStart, layout.x);
      xEnd = Math.max(xEnd, layout.x + layout.width);
      yEnd = Math.max(yEnd, layout.y + layout.height);
    }
    if (isFullWidthComponent(element)) {
      fullWidth = true;
    }
  });

  if (fullWidth) {
    xStart = 0;
    xEnd = pageWidth;
  }

  return {yStart, xStart, yEnd, xEnd, width: xEnd - xStart, height: yEnd - yStart};
}

function getMaxDeltaY(viewState, state, elements) {
  const pageElement = getPageElement(state[viewState].staticElements);
  const boundingLayout = getBoundingLayout(elements);
  return pageElement.layout.height - boundingLayout.yEnd;
}

export {
  getUpdatedElementPosition,
  getUpdatedElementPositionByDelta,
  getYPositionAtMiddleOfView,
  getDraggableDirections,
  getBoundingLayout,
  getMaxDeltaY,
};
